import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, MenuItem, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { BackdropLoader, Icon, SelectMenu, SimpleModal } from '@livable/saas-react-components';
import { useStore } from 'src/stores';
import { getCommonPricings } from '../initialState';
import useStyles from './styles';
function formatPrice(val) {
  return typeof val === 'number' && !Number.isNaN(val) ? "$".concat(Number(val).toFixed(2)) : '-';
}
function mapStorePriceToItem(p) {
  var _tierTable$tierPrice$, _tierTable$tierPrice;
  const isTiered = (p.priceType || '').toLowerCase() === 'tiered';
  const tierTable = p.tierTableResponse;
  const tierAmounts = (_tierTable$tierPrice$ = tierTable === null || tierTable === void 0 || (_tierTable$tierPrice = tierTable.tierPrice) === null || _tierTable$tierPrice === void 0 ? void 0 : _tierTable$tierPrice.map(t => t.amount).filter(n => typeof n === 'number')) !== null && _tierTable$tierPrice$ !== void 0 ? _tierTable$tierPrice$ : [];
  const minFromPrice = typeof p.minimumPrice === 'number' && !Number.isNaN(p.minimumPrice) ? p.minimumPrice : null;
  const minPrice = minFromPrice !== null && minFromPrice !== void 0 ? minFromPrice : isTiered && tierTable ? typeof tierTable.minimumPrice === 'number' && !Number.isNaN(tierTable.minimumPrice) ? tierTable.minimumPrice : tierAmounts.length ? Math.min(...tierAmounts) : null : null;
  const amount = isTiered && minPrice != null ? "Min ".concat(formatPrice(minPrice)) : formatPrice(p.fixedPrice);
  return {
    id: p.id,
    code: p.id,
    name: p.priceName,
    priceName: p.priceName,
    type: p.priceType,
    productId: p.productId,
    productName: p.productName,
    amount
  };
}
export const ManagePricingsModal = observer(function ManagePricingsModal(_ref) {
  var _store$stripe$prices, _store$stripe$bulkUpd, _store$stripe$bulkUpd2;
  let isOpen = _ref.isOpen,
    onClose = _ref.onClose,
    selectedIds = _ref.selectedIds,
    subscriptions = _ref.subscriptions,
    onSave = _ref.onSave;
  const classes = useStyles();
  const store = useStore();
  const selectedSubs = subscriptions.filter(s => selectedIds.includes(s.id));
  const initialCommon = getCommonPricings(selectedSubs);
  const allPricesFromStore = ((_store$stripe$prices = store.stripe.prices) !== null && _store$stripe$prices !== void 0 ? _store$stripe$prices : []).map(mapStorePriceToItem);
  const _useState = useState(initialCommon),
    _useState2 = _slicedToArray(_useState, 2),
    commonPricings = _useState2[0],
    setCommonPricings = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    addValue = _useState4[0],
    setAddValue = _useState4[1];
  useEffect(() => {
    if (isOpen) {
      setCommonPricings(getCommonPricings(subscriptions.filter(s => selectedIds.includes(s.id))));
      setAddValue('');
    }
  }, [isOpen, selectedIds, subscriptions]);
  const priceIdsInAnySelectedSub = (() => {
    const set = new Set();
    selectedSubs.forEach(sub => {
      var _ref2, _sub$prices;
      const prices = (_ref2 = (_sub$prices = sub.prices) !== null && _sub$prices !== void 0 ? _sub$prices : sub.pricings) !== null && _ref2 !== void 0 ? _ref2 : [];
      prices.forEach(p => {
        var _p$id;
        const k = (_p$id = p.id) !== null && _p$id !== void 0 ? _p$id : p.code;
        if (k) set.add(k);
      });
    });
    return set;
  })();
  const commonCodes = new Set(commonPricings.map(p => p.code || p.id));
  const availableToAdd = allPricesFromStore.filter(p => !priceIdsInAnySelectedSub.has(p.id || p.code) && !commonCodes.has(p.id || p.code));
  const initialCodes = new Set(initialCommon.map(p => p.code));
  const currentCodes = new Set(commonPricings.map(p => p.code));
  const hasChanges = initialCodes.size !== currentCodes.size || [...initialCodes].some(c => !currentCodes.has(c)) || [...currentCodes].some(c => !initialCodes.has(c));
  const handleAdd = codeOrId => {
    const toAdd = allPricesFromStore.find(p => (p.id || p.code) === codeOrId);
    if (toAdd && !commonCodes.has(toAdd.id || toAdd.code)) {
      setCommonPricings(prev => [...prev, _objectSpread({}, toAdd)]);
      setAddValue('');
    }
  };
  const handleRemove = code => {
    setCommonPricings(prev => prev.filter(p => p.code !== code));
  };
  const isLoading = (_store$stripe$bulkUpd = (_store$stripe$bulkUpd2 = store.stripe.bulkUpdateSubscriptionsAsync) === null || _store$stripe$bulkUpd2 === void 0 ? void 0 : _store$stripe$bulkUpd2.inProgress) !== null && _store$stripe$bulkUpd !== void 0 ? _store$stripe$bulkUpd : false;
  const handleSave = async () => {
    const ids = Array.isArray(selectedIds) ? selectedIds.filter(Boolean) : [];
    const priceIdsList = commonPricings.map(p => p.id || p.code).filter(Boolean);
    if (ids.length === 0) {
      onSave === null || onSave === void 0 || onSave(commonPricings);
      onClose === null || onClose === void 0 || onClose();
      return;
    }
    try {
      await store.stripe.bulkUpdateSubscriptionsAsync.run({
        subscriptionIds: ids,
        priceIds: priceIdsList.length > 0 ? priceIdsList : null,
        status: null
      });
      onSave === null || onSave === void 0 || onSave(commonPricings);
      onClose === null || onClose === void 0 || onClose();
    } catch (err) {
      console.error('Bulk update subscriptions (pricings) failed:', err);
    }
  };
  const handleClose = () => {
    setCommonPricings(initialCommon);
    setAddValue('');
    onClose();
  };
  const selectedCount = selectedIds.length;
  return /*#__PURE__*/React.createElement(SimpleModal, {
    trigger: null,
    isOpen: isOpen,
    handleClose: handleClose,
    title: "Manage pricings",
    subTitle: "Selected ".concat(selectedCount, " subscription").concat(selectedCount !== 1 ? 's' : ''),
    rightButton: /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleSave,
      disabled: !hasChanges || isLoading
    }, isLoading ? 'Saving...' : 'Save changes')
  }, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: isLoading
  }), /*#__PURE__*/React.createElement(Box, {
    className: classes.content
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4",
    className: classes.sectionTitle
  }, "Pricings"), /*#__PURE__*/React.createElement(Box, {
    className: classes.addControl
  }, /*#__PURE__*/React.createElement(SelectMenu, {
    id: "add-pricings",
    labelId: "add-pricings-label",
    label: "Add pricings",
    value: addValue,
    onChange: e => {
      const v = e.target.value;
      if (v) handleAdd(v);
      setAddValue('');
    }
  }, availableToAdd.map(p => {
    var _p$name, _ref3, _p$productName;
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: p.id || p.code,
      value: p.id || p.code
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1",
      fontWeight: 600
    }, (_p$name = p.name) !== null && _p$name !== void 0 ? _p$name : p.priceName), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "text.secondary"
    }, (_ref3 = (_p$productName = p.productName) !== null && _p$productName !== void 0 ? _p$productName : p.id) !== null && _ref3 !== void 0 ? _ref3 : p.code, " - ", p.type)));
  }), availableToAdd.length === 0 && /*#__PURE__*/React.createElement(MenuItem, {
    value: "",
    disabled: true
  }, "No more pricings to add"))), /*#__PURE__*/React.createElement(Box, {
    className: classes.list
  }, commonPricings.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "No common pricings. Add pricings above.") : commonPricings.map(p => {
    var _ref4, _p$productName2;
    return /*#__PURE__*/React.createElement(Box, {
      key: p.code,
      className: classes.listRow
    }, /*#__PURE__*/React.createElement(Box, {
      className: classes.listRowMain
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1",
      fontWeight: 600
    }, p.name), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "text.secondary"
    }, (_ref4 = (_p$productName2 = p.productName) !== null && _p$productName2 !== void 0 ? _p$productName2 : p.productId) !== null && _ref4 !== void 0 ? _ref4 : p.code, " - ", p.type), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "text.secondary"
    }, "Used in ", selectedCount, " subscription", selectedCount !== 1 ? 's' : '')), /*#__PURE__*/React.createElement(Box, {
      className: classes.listRowRight
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "h5"
    }, p.amount), /*#__PURE__*/React.createElement(IconButton, {
      onClick: () => handleRemove(p.code),
      "aria-label": "Remove pricing"
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "close"
    }))));
  }))));
});